import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Loader2, X } from "lucide-react";
import { crud } from "../lib/api";
import { FK_RESOURCES } from "../lib/choices";

function sameId(a, b) {
    if (a == null || b == null) return false;
    return String(a) === String(b);
}

function toIdList(value) {
    if (value == null || value === "") return [];
    const arr = Array.isArray(value) ? value : [value];
    return arr
        .flatMap((item) => {
            if (item == null || item === "") return [];
            if (Array.isArray(item)) return item;
            if (typeof item === "object" && item.id != null) {
                return Array.isArray(item.id) ? item.id : [item.id];
            }
            return [item];
        })
        .map((id) => (typeof id === "number" ? id : Number(String(id))))
        .filter((id) => !Number.isNaN(id));
}

/**
 * Müvekkil.aracilar M2M — tek amaçlı, generic EntityForm'dan bağımsız.
 */
export default function AracilarMultiField({
    value,
    onChange,
    placeholder = "Birden fazla aracı seçebilirsiniz",
    testId,
}) {
    const cfg = FK_RESOURCES.aracilar || {
        labelFor: (o) => o?.ad || o?.name || `#${o?.id}`,
        secondary: () => "",
    };

    const ids = useMemo(() => toIdList(value), [value]);
    const idsKey = ids.join(",");

    const [labels, setLabels] = useState(() => new Map());
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const wrapRef = useRef(null);
    const debounceRef = useRef(null);

    useEffect(() => {
        let cancel = false;
        async function hydrate() {
            if (ids.length === 0) return;
            const missing = ids.filter((id) => !labels.has(String(id)));
            if (missing.length === 0) return;
            const fetched = await Promise.all(
                missing.map((id) =>
                    crud.retrieve("aracilar", id).catch(() => ({ id })),
                ),
            );
            if (cancel) return;
            setLabels((prev) => {
                const next = new Map(prev);
                for (const row of fetched) {
                    if (row?.id != null) next.set(String(row.id), row);
                }
                return next;
            });
        }
        hydrate();
        return () => {
            cancel = true;
        };
    }, [idsKey]);

    useEffect(() => {
        function onDocClick(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    useEffect(() => {
        if (!open) return;
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const params = { page_size: 80, aktif: true };
                if (query.trim()) params.search = query.trim();
                const data = await crud.list("aracilar", params);
                const list = Array.isArray(data) ? data : data.results || [];
                setResults(list);
            } catch {
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 200);
        return () => clearTimeout(debounceRef.current);
    }, [query, open]);

    function labelFor(id) {
        const row = labels.get(String(id));
        if (!row) return `#${id}`;
        return cfg.labelFor(row);
    }

    function add(row) {
        if (ids.some((id) => sameId(id, row.id))) return;
        const next = [...ids, row.id];
        onChange?.(next);
        setLabels((prev) => {
            const m = new Map(prev);
            m.set(String(row.id), row);
            return m;
        });
        setQuery("");
    }

    function remove(id) {
        onChange?.(ids.filter((itemId) => !sameId(itemId, id)));
    }

    return (
        <div ref={wrapRef} className="relative">
            <div
                data-testid={testId}
                onClick={() => setOpen(true)}
                className="w-full bg-[#0B0F19] border border-white/10 rounded-md px-2 py-1.5 text-sm flex flex-wrap gap-1.5 items-center min-h-[44px] cursor-text focus-within:border-[#D4AF37] focus-within:ring-1 focus-within:ring-[#D4AF37]"
            >
                {ids.length === 0 && (
                    <span className="text-slate-600 px-1 text-xs">{placeholder}</span>
                )}
                {ids.map((id) => (
                    <span
                        key={String(id)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#F3E5AB] text-xs"
                    >
                        <span className="truncate max-w-[180px]">{labelFor(id)}</span>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                remove(id);
                            }}
                            className="hover:text-rose-300 shrink-0"
                            aria-label="Kaldır"
                        >
                            <X className="h-3 w-3" />
                        </button>
                    </span>
                ))}
                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => setOpen(true)}
                    placeholder={ids.length ? "Başka aracı ekle…" : ""}
                    className="flex-1 min-w-[100px] bg-transparent outline-none px-1 text-sm text-slate-100 placeholder:text-slate-600"
                />
                <ChevronDown
                    className={`h-4 w-4 text-slate-500 shrink-0 transition-transform ${
                        open ? "rotate-180" : ""
                    }`}
                />
            </div>
            {ids.length > 0 && (
                <p className="mt-1 text-[10px] text-slate-500">
                    {ids.length} aracı seçili — listeden ekleyebilir veya chip ile kaldırabilirsiniz.
                </p>
            )}

            {open && (
                <div className="absolute z-[90] mt-1 w-full rounded-md border border-white/10 bg-[#0F1626] shadow-2xl overflow-hidden max-h-64 overflow-y-auto">
                    {loading ? (
                        <div className="px-3 py-4 text-xs text-slate-500 flex items-center gap-2">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Aranıyor…
                        </div>
                    ) : results.length === 0 ? (
                        <div className="px-3 py-4 text-xs text-slate-500">Sonuç yok.</div>
                    ) : (
                        results.map((row) => {
                            const picked = ids.some((id) => sameId(id, row.id));
                            return (
                                <button
                                    key={row.id}
                                    type="button"
                                    disabled={picked}
                                    onClick={() => add(row)}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-white/5 ${
                                        picked ? "text-slate-500 cursor-default" : "text-slate-100"
                                    }`}
                                >
                                    {cfg.labelFor(row)}
                                    {picked ? " ✓" : ""}
                                </button>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
}

export { toIdList as aracilarToIdList };
